import { FilterTableSettings, SortDirection } from '@gea/digital-ui-lib';
import { Annunciation, AnnunciationTree, MachineFaultDto } from '@shared/models';
import { TreeNode } from 'primeng/api';

/* ------------------------------- Helpers -------------------------------- */

/** Narrow unknown to object-like */
function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Node has `data` key ? we treat it as a TreeNode-like */
function isTreeNodeLike<T>(v: unknown): v is TreeNode<T> {
  return isObject(v) && 'data' in v;
}

/* ------------------------------ Core utils ------------------------------ */

export function sortTableData(tableFilter: FilterTableSettings, data: Annunciation[]) {
  const keys = Object.keys(tableFilter.columns ?? {});
  const columns = keys.map((key) => {
    // Safely access column settings and normalize sort direction
    const colSettings = (tableFilter.columns as Record<string, { sort?: unknown }>)[key];
    return { key, sortDirection: colSettings?.sort as SortDirection };
  });

  columns.forEach((column) => {
    if (!column) return;
    if (!column.sortDirection) return;

    const key = column.key;
    data = [...data].sort((a, b) => {
      // Safe dynamic access; treat unknowns carefully
      const av = (a as unknown as Record<string, unknown>)[key];
      const bv = (b as unknown as Record<string, unknown>)[key];

      if (typeof av === 'string' && typeof bv === 'string') {
        return sortStrings(av, bv, column.sortDirection);
      }

      if (typeof av === 'number' && typeof bv === 'number') {
        return sortNumbers(av, bv, column.sortDirection);
      }

      return 0;
    });
  });

  return data;
}

export function safeValue(value: unknown, key: string): string | number | Date | null {
  if (value == null) return null;

  if (['timeStamp', 'scoredAt', 'solved'].includes(key)) {
    const d = new Date(String(value));
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === 'number' || typeof value === 'string') return value;

  return String(value);
}

export function toTreeNodes(data: Annunciation[] | AnnunciationTree[]): AnnunciationTree[] {
  if (!Array.isArray(data) || data.length === 0) return [];

  const first = data[0];

  if (isTreeNodeLike<MachineFaultDto>(first)) {
    // Already nodes -> deep clone to keep them extensible
    return cloneTreeNodes<MachineFaultDto>(data as AnnunciationTree[]) as AnnunciationTree[];
  }

  // Convert raw items to nodes
  return (data as Annunciation[]).map((d) => ({
    data: { ...(d as unknown as MachineFaultDto) },
    children: [],
  }));
}

/**
 * Deep clone of TreeNode[] that:
 *  - returns NEW node objects (extensible)
 *  - returns NEW shallow data objects
 *  - recurses children
 *  - omits parent (PrimeNG will assign)
 */
export function cloneTreeNodes<T>(nodes: readonly TreeNode<T>[]): TreeNode<T>[] {
  const clone = (n: TreeNode<T>): TreeNode<T> => {
    // Shallow-clone data (objects only), passthrough primitives/arrays/undefined
    const clonedData = (() => {
      if (n.data === undefined) return undefined;
      const d = n.data as unknown;
      if (Array.isArray(d)) return n.data; // keep arrays as-is
      if (d !== null && typeof d === 'object') {
        return { ...(d as Record<string, unknown>) } as T;
      }
      return n.data;
    })();

    const cloned: TreeNode<T> = {
      ...n,
      data: clonedData,
      children: Array.isArray(n.children) ? n.children.map(clone) : undefined,
    };

    // Ensure `parent` is not carried over (PrimeNG will set it)
    delete (cloned as Record<string, unknown>).parent;

    return cloned;
  };

  return nodes.map(clone);
}

/**
 * Sorts nodes by FilterTableSettings.
 * - Returns NEW nodes (so they�re extensible).
 * - Recursively sorts children.
 * - Later column sorts dominate (matches your previous behavior).
 */
export function sortTreeNodes<T>(tableFilter: FilterTableSettings, nodes: TreeNode<T>[]): TreeNode<T>[] {
  if (!nodes?.length) return [];

  let sorted = [...nodes];

  const keys = Object.keys(tableFilter?.columns ?? {});
  const columnSettings = tableFilter.columns as Record<string, { sort?: unknown }>;
  const columns = keys.map((key) => {
    const sort = columnSettings?.[key]?.sort;
    const sortDirection: 'asc' | 'desc' | undefined =
      sort === 'asc' || sort === 'desc'
        ? sort
        : sort === SortDirection.ASCENDING
          ? 'asc'
          : sort === SortDirection.DESCENDING
            ? 'desc'
            : undefined;
    return { key, sortDirection };
  });

  for (const col of columns) {
    if (!col?.sortDirection) continue;

    const dir = col.sortDirection === 'desc' ? -1 : 1;
    const key = col.key;

    sorted = sorted.sort((a, b) => {
      const leftData = a?.data as unknown as Record<string, unknown> | undefined;
      const rightData = b?.data as unknown as Record<string, unknown> | undefined;
      const va = safeValue(leftData?.[key], key);
      const vb = safeValue(rightData?.[key], key);

      if (va == null && vb == null) return 0;
      if (va == null) return -1 * dir;
      if (vb == null) return 1 * dir;

      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * dir;
      }
      if (va instanceof Date && vb instanceof Date) {
        return (va.getTime() - vb.getTime()) * dir;
      }

      const sa = String(va).toLocaleString().toLowerCase();
      const sb = String(vb).toLocaleString().toLowerCase();
      return sa.localeCompare(sb) * dir;
    });
  }

  // Rebuild as NEW nodes, recurse children, and shallow-clone `data`
  const rebuilt: TreeNode<T>[] = sorted.map((n) => {
    const newChildren = n.children?.length ? sortTreeNodes(tableFilter, n.children) : undefined;

    const node: TreeNode<T> = {
      ...n,
      data: n.data ? ({ ...(n.data as unknown as Record<string, unknown>) } as T) : ({} as T),
      children: newChildren,
    };

    // Drop `parent` from the rebuilt node
    delete (node as Record<string, unknown>).parent;

    return node;
  });

  return rebuilt;
}

/** Optional helpers (unchanged) */
export function sortStrings(a: string, b: string, sortDirection: SortDirection) {
  return sortDirection === SortDirection.ASCENDING ? a.localeCompare(b) : b.localeCompare(a);
}

export function sortNumbers(a: number, b: number, sortDirection: SortDirection) {
  return sortDirection === SortDirection.ASCENDING ? a - b : b - a;
}
